import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { JobModalPropsType } from './types';
import { Button } from '@mollybet/ui';
import {
  JobModalContent,
  JobModalLogs,
  JobModalRunInfo,
  JobModalStatus,
  JobModalSummaryLine,
  JobModalType,
  ModalButtons,
  ShowLogsButton,
  SpacebetweenInfo,
} from './elements';
import { StatusIcon } from '../statusIcon';
import {
  HOUR_IN_MS,
  MINUTE_IN_MS,
  SECOND_IN_MS,
} from '../../../common/utils/time';
import { DangerButton } from 'common/components/buttons';
import {
  doCancelJob,
  doGetJobLogs,
  doGetJobs,
} from 'common/state/jobs/thunkActions';
import { RUNNING_JOB_STATUSES } from '../config';
import { useDateTime } from '../hooks';

const SHRUNK_LOGS_COUNT = 16;

export const JobModal = ({
  job,
  logs,
  setModalMode,
  setCreateInitialData,
  jobsDispatch,
  logsDispatch,
  cancelJobState,
  cancelJobDispatch,
  closeModal,
}: JobModalPropsType) => {
  const [showAllLogs, setShowAllLogs] = useState(false);

  const started = useMemo(() => job.started && new Date(job.started), [job]);
  const finished = useMemo(() => job.finished && new Date(job.finished), [job]);
  const now = useDateTime();
  const durationString = useMemo(() => {
    const dt = +(finished || now) - +started;
    const hours = Math.floor(dt / HOUR_IN_MS);
    const minutes = Math.floor(dt / MINUTE_IN_MS) % 60;
    const seconds = Math.floor(dt / SECOND_IN_MS) % 60;

    if (dt < MINUTE_IN_MS) return `${Math.ceil(dt / 1000)}s`;
    if (dt < HOUR_IN_MS) return `${minutes}m ${seconds}s`;
    return `${hours}h ${minutes}m`;
  }, [started, finished, now]);

  useEffect(() => {
    logsDispatch(doGetJobLogs(job.id));
    if (RUNNING_JOB_STATUSES.has(job.status))
      jobsDispatch(doGetJobs({ id: job.id }));
    // eslint-disable-next-line
  }, []);

  const cancelJob = useCallback(
    () => cancelJobDispatch(doCancelJob(job.id)),
    [cancelJobDispatch, job]
  );

  return (
    <JobModalContent>
      <JobModalSummaryLine>
        <span>{job.params.date}</span>
        <JobModalType>{job.type.replaceAll('_', ' ')}</JobModalType>
        <JobModalStatus>
          <StatusIcon status={job.status} size={0.75} />
          <span>{job.status}</span>
        </JobModalStatus>
      </JobModalSummaryLine>
      <JobModalRunInfo>
        {job.started && job.finished ? (
          <div>
            Started at {started.toLocaleString()} and took {durationString} to
            run.
          </div>
        ) : job.started ? (
          <SpacebetweenInfo>
            <span>
              Started at {started.toLocaleString()} and has been running for{' '}
              {durationString} so far.
            </span>
            <DangerButton busy={cancelJobState.pending} onClick={cancelJob}>
              Cancel
            </DangerButton>
          </SpacebetweenInfo>
        ) : (
          <SpacebetweenInfo>
            <span>Job {job.status}...</span>
            <DangerButton busy={cancelJobState.pending} onClick={cancelJob}>
              Cancel
            </DangerButton>
          </SpacebetweenInfo>
        )}

        <ShowLogsButton onClick={() => setShowAllLogs(!showAllLogs)}>
          {logs
            ? showAllLogs
              ? `Show last ${SHRUNK_LOGS_COUNT} logs`
              : 'Show all logs'
            : null}
        </ShowLogsButton>
        <JobModalLogs>
          {logs
            ? showAllLogs
              ? logs
              : logs.split('\n').slice(-SHRUNK_LOGS_COUNT).join('\n')
            : 'No logs...'}
        </JobModalLogs>
      </JobModalRunInfo>
      <ModalButtons>
        <Button
          variant="primary"
          onClick={() => {
            setModalMode('create');
            setCreateInitialData({
              date_from: job.params.date,
              date_to: job.params.date,
              job_types: [job.type],
            });
          }}
        >
          Create Similar
        </Button>
        <Button onClick={closeModal}>Close</Button>
      </ModalButtons>
    </JobModalContent>
  );
};
